/**
 * FR-005 · Tìm kiếm toàn cục trên thanh đầu trang.
 *
 * - Phím tắt `/` đưa tiêu điểm vào ô (bỏ qua khi đang gõ trong input khác).
 * - Gợi ý tên miền · bản ghi DNS · người dùng (chỉ Admin) — phạm vi do máy chủ
 *   quyết định bằng `visibleDomains()`, FE không lọc lại (BR-1).
 * - Điều hướng ↑ ↓ Enter Esc, `role="listbox"` + `aria-activedescendant`
 *   (docs/08 §8.7).
 *
 * Giữ nguyên markup `label.search > svg + input + kbd` của prototype.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import { useFormat } from '../lib/format';
import { Icon } from './Icon';

interface SearchDomainHit {
  id: string;
  name: string;
  status: string;
  relation: 'owned' | 'granted';
  recordCount: number;
}

interface SearchRecordHit {
  id: string;
  domainId: string;
  domainName: string;
  type: string;
  name: string;
  content: string;
}

interface SearchUserHit {
  id: string;
  fullName: string;
  email: string;
  roleName: string;
  status: string;
}

interface SearchResult {
  q: string;
  domains: SearchDomainHit[];
  records: SearchRecordHit[];
  users: SearchUserHit[];
  canSearchUsers: boolean;
}

interface Row {
  key: string;
  group: 'domains' | 'records' | 'users';
  label: string;
  hint: string;
  to: string;
}

const MIN_LENGTH = 2;
const DEBOUNCE_MS = 250; // FR-022 dùng cùng nhịp

/**
 * Người dùng có đang GÕ ở chỗ khác không? Nếu có thì phím tắt `/` phải im.
 *
 * Tách ra khỏi handler vì hai lẽ:
 *
 * 1. `isContentEditable` **không tồn tại trong jsdom** (`undefined`, không phải
 *    `false`). Nhánh dựa vào một mình nó là nhánh test không chạm tới được — nó
 *    có thể mục ruỗng mà không gì báo. Nên hỏi thêm bằng **thuộc tính DOM**,
 *    thứ jsdom có thật.
 *
 * 2. `closest()` còn ĐÚNG HƠN cho câu hỏi cần hỏi: `contenteditable` **kế
 *    thừa** — con của một vùng soạn thảo cũng là chỗ đang gõ, nhưng bản thân nó
 *    không mang thuộc tính. Kiểm mỗi phần tử đang giữ tiêu điểm sẽ bỏ sót.
 *    `:not([contenteditable="false"])` để vùng cố ý TẮT soạn thảo không bị tính
 *    nhầm.
 *
 * Giữ luôn `isContentEditable`: trên trình duyệt thật nó phủ được cả
 * `designMode`, thứ không suy ra từ thuộc tính nào.
 */
function dangGoOChoKhac(active: Element | null): boolean {
  const tag = active?.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (!(active instanceof HTMLElement)) return false;
  if (active.isContentEditable) return true;
  return active.closest('[contenteditable]:not([contenteditable="false"])') !== null;
}

export function GlobalSearch() {
  const { formatNumber } = useFormat();
  const t = useT();
  const navigate = useNavigate();

  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);

  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [term]);

  // Phím tắt `/` — bỏ qua khi tiêu điểm đang ở ô nhập khác (FR-005).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (dangGoOChoKhac(document.activeElement)) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const query = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<SearchResult>(`/search?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= MIN_LENGTH,
    staleTime: 15_000,
  });

  const rows = useMemo<Row[]>(() => {
    const data = query.data;
    if (!data) return [];
    return [
      ...data.domains.map((domain) => ({
        key: `domain-${domain.id}`,
        group: 'domains' as const,
        label: domain.name,
        hint: `${t(`domains.relation.${domain.relation}`)} · ${formatNumber(domain.recordCount)}`,
        to: `/domains/${domain.id}`,
      })),
      ...data.records.map((record) => ({
        key: `record-${record.id}`,
        group: 'records' as const,
        label: `${record.type} ${record.name}`,
        hint: `${record.content} · ${record.domainName}`,
        to: `/domains/${record.domainId}?tab=dns`,
      })),
      ...data.users.map((user) => ({
        key: `user-${user.id}`,
        group: 'users' as const,
        label: user.fullName || user.email,
        hint: `${user.email} · ${user.roleName}`,
        to: `/admin/users?q=${encodeURIComponent(user.email)}`,
      })),
    ];
  }, [query.data, t]);

  useEffect(() => setCursor(0), [rows.length]);

  const go = (row: Row) => {
    setOpen(false);
    setTerm('');
    setDebounced('');
    navigate(row.to);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setCursor((value) => Math.min(rows.length - 1, value + 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((value) => Math.max(0, value - 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const row = rows[cursor];
      if (row) go(row);
    }
  };

  const showPanel = open && debounced.length >= MIN_LENGTH;

  return (
    <div className="search-wrap" ref={boxRef}>
      <label className="search" data-od-id="global-search">
        <Icon name="search" size={16} />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="global-search-results"
          aria-autocomplete="list"
          {...(showPanel && rows[cursor]
            ? { 'aria-activedescendant': `gs-${rows[cursor].key}` }
            : {})}
          placeholder={t('nav.searchPlaceholder')}
          aria-label={t('common.search')}
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {/*
         * Nút xoá cho thanh tìm kiếm toàn cục (OQ-230).
         *
         * Không dùng `SearchBox` ở đây: ô này có `combobox` + điều hướng bàn
         * phím riêng, gói vào component chung sẽ kéo theo cả tá prop chỉ dùng
         * một lần. Nhưng nút xoá thì giống hệt — người dùng không nên phải học
         * hai cách xoá cho hai ô trông như nhau.
         */}
        {term ? (
          <button
            type="button"
            className="search-clear"
            aria-label={t('common.clearSearch')}
            title={t('common.clearSearch')}
            data-od-id="search-clear"
            onMouseDown={(event) => {
              /* Giữ tiêu điểm trong ô: mất tiêu điểm sẽ đóng bảng gợi ý. */
              event.preventDefault();
            }}
            onClick={() => {
              setTerm('');
              inputRef.current?.focus();
            }}
          >
            <Icon name="close" size={14} />
          </button>
        ) : null}
        <kbd>/</kbd>
      </label>

      {showPanel ? (
        <div
          className="search-pop card"
          id="global-search-results"
          role="listbox"
          aria-label={t('common.search')}
        >
          {query.isPending ? (
            <div className="dsw-empty">{t('common.loading')}</div>
          ) : rows.length === 0 ? (
            <div className="dsw-empty">{t('search.empty')}</div>
          ) : (
            (['domains', 'records', 'users'] as const).map((group) => {
              const items = rows.filter((row) => row.group === group);
              if (items.length === 0) return null;
              return (
                <div key={group} className="search-group">
                  <span className="eyebrow">{t(`search.group.${group}`)}</span>
                  {items.map((row) => (
                    <button
                      key={row.key}
                      id={`gs-${row.key}`}
                      type="button"
                      role="option"
                      aria-selected={rows[cursor]?.key === row.key}
                      onMouseEnter={() => setCursor(rows.indexOf(row))}
                      onClick={() => go(row)}
                    >
                      <span className="who">
                        <b>{row.label}</b>
                        <span>{row.hint}</span>
                      </span>
                    </button>
                  ))}
                </div>
              );
            })
          )}
          {query.data && !query.data.canSearchUsers ? (
            <div className="search-note small muted">{t('search.usersAdminOnly')}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
